import React, {useEffect, useState} from "react";
import usePlacesAutocomplete, {getGeocode, getLatLng} from "use-places-autocomplete";
import {useTranslation} from "react-i18next";
import {Box, CircularProgress, ListItemButton, ListItemIcon, ListItemText, Paper, TextField, Typography,} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import {GoogleMap, Marker} from "@react-google-maps/api";

const AddressAutocomplete = ({
                                 label,
                                 value,
                                 latitude,
                                 longitude,
                                 onSelect,
                                 error,
                                 helperText,
                                 isLoaded,
                             }) => {
    const {t} = useTranslation();
    const {
        ready,
        value: internalValue,
        suggestions: {status, data},
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: {componentRestrictions: {country: "tr"}},
        debounce: 300,
    });

    const [mapCenter, setMapCenter] = useState(null);
    const [addressDetails, setAddressDetails] = useState(null);
    const [activeIndex, setActiveIndex] = useState(-1);

    useEffect(() => {
        if (isLoaded && value !== internalValue) setValue(value || "", false);
        if (latitude && longitude) setMapCenter({lat: latitude, lng: longitude});
    }, [isLoaded, value, latitude, longitude]);


    if (!isLoaded) {
        return (
            <TextField
                label={label}
                fullWidth
                value={value || ""}
                disabled
                InputProps={{
                    endAdornment: <CircularProgress size={18}/>,
                }}
            />
        );
    }

    const handleSelect = async (address) => {
        setValue(address, false);
        clearSuggestions();
        setActiveIndex(-1);

        if (!address?.trim()) {
            setMapCenter(null);
            setAddressDetails(null);
            onSelect("", null, null, null);
            return;
        }

        try {
            const results = await getGeocode({address});
            const {lat, lng} = await getLatLng(results[0]);
            setMapCenter({lat, lng});

            const comps = results[0].address_components;
            const city =
                comps.find((c) => c.types.includes("administrative_area_level_1"))?.long_name || "";
            const district =
                comps.find((c) => c.types.includes("administrative_area_level_2"))?.long_name ||
                comps.find((c) => c.types.includes("administrative_area_level_3"))?.long_name ||
                "";
            const neighborhood =
                comps.find((c) => c.types.includes("sublocality"))?.long_name ||
                comps.find((c) => c.types.includes("route"))?.long_name ||
                "";

            setAddressDetails({city, district, neighborhood});
            onSelect(address, lat, lng, {city, district, neighborhood});
        } catch (err) {
            console.error("Geocode error:", err);
            setMapCenter(null);
            setAddressDetails(null);
            onSelect(address, null, null, null);
        }
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setValue(val);
        if (!val.trim()) {
            setMapCenter(null);
            setAddressDetails(null);
            onSelect("", null, null, null);
        }
    };


    const handleKeyDown = (e) => {
        if (status !== "OK" || data.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) => (prev + 1) % data.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) => (prev - 1 + data.length) % data.length);
        } else if (e.key === "Enter" && activeIndex >= 0) {
            e.preventDefault();
            handleSelect(data[activeIndex].description);
        } else if (e.key === "Tab" && activeIndex >= 0) {
            handleSelect(data[activeIndex].description);
        } else if (e.key === "Escape") {
            clearSuggestions();
            setActiveIndex(-1);
        }
    };

    return (
        <Box sx={{position: "relative"}}>
            <TextField
                label={label}
                fullWidth
                value={internalValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                error={error}
                helperText={helperText}
                disabled={!ready}
                InputProps={{
                    endAdornment: !ready ? <CircularProgress size={18}/> : null,
                }}
            />


            {status === "OK" && (
                <Paper
                    sx={{
                        position: "absolute",
                        zIndex: 10,
                        width: "100%",
                        mt: 0.5,
                        maxHeight: 240,
                        overflowY: "auto",
                        borderRadius: 2,
                        boxShadow: 3,
                    }}
                >
                    {data.map(({place_id, description}, idx) => (
                        <ListItemButton
                            key={place_id}
                            onClick={() => handleSelect(description)}
                            selected={idx === activeIndex}
                            sx={{
                                "&.Mui-selected": {
                                    bgcolor: "action.hover",
                                    borderLeft: "3px solid",
                                    borderColor: "primary.main",
                                },
                                "&:hover": {bgcolor: "action.hover"},
                            }}
                        >
                            <ListItemIcon sx={{minWidth: 36, color: "primary.main"}}>
                                <PlaceIcon fontSize="small"/>
                            </ListItemIcon>
                            <ListItemText
                                primary={description}
                                primaryTypographyProps={{fontSize: 14}}
                            />
                        </ListItemButton>
                    ))}
                </Paper>
            )}


            {mapCenter && (
                <Box
                    sx={{
                        height: 200,
                        width: "100%",
                        mt: 1,
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: 2,
                    }}
                >
                    <GoogleMap
                        mapContainerStyle={{width: "100%", height: "100%"}}
                        center={mapCenter}
                        zoom={15}
                        options={{disableDefaultUI: true}}
                    >
                        <Marker position={mapCenter}/>
                    </GoogleMap>
                </Box>
            )}


            {addressDetails && (
                <Box sx={{mt: 1, pl: 0.5}}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{t("address.city")}:</strong> {addressDetails.city || t("common.notAvailable")}{" "}
                        <strong>{t("address.district")}:</strong> {addressDetails.district || t("common.notAvailable")}{" "}
                        <strong>{t("address.neighborhood")}:</strong> {addressDetails.neighborhood || t("common.notAvailable")}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default AddressAutocomplete;
